import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { memberFullName, ROLE_LABELS, STATUS_COLOURS, STATUS_LABELS } from '../../../library/membershipDisplay';
import { IClinic, IClinicMember } from '../../../library/models/clinic';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { RecordCard } from '../../recordCard/RecordCard';
import { cardListStyles } from '../../recordCard/recordCard.styles';
import { alertStyles, emptyStateBoxStyles, headerCellStyles, identifierCellStyles, loadingBoxStyles, tableContainerStyles } from './clinics.styles';
import { dialogActionsStyles, dialogTitleStyles, memberRowStyles, subtitleStyles } from './clinicMembersDialog.styles';

interface ClinicMembersDialogProps {
    isOpen: boolean;
    clinic: IClinic | null;
    members: IClinicMember[];
    isLoading: boolean;
    /** False while the request is in flight or after it failed -- see Clinics.tsx. */
    hasLoaded: boolean;
    onClose: () => void;
    onInviteClick: () => void;
}

/**
 * Who belongs to a clinic, invitations included.
 *
 * A presenter only: the query lives on the page, so that a successful invitation can
 * refetch it while this stays open. Uses no router hooks.
 */
export const ClinicMembersDialog = (props: ClinicMembersDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const isCompact = useIsCompact();

    return (
        <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="md" fullWidth fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.CLINIC_MEMBERS_TITLE, {
                    clinicName: props.clinic?.displayName ?? '',
                })}
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" sx={subtitleStyles}>
                    {translate(ResourcesIds.CLINIC_MEMBERS_SUBTITLE)}
                </Typography>

                {!props.isLoading && !props.hasLoaded && (
                    <Alert severity="error" sx={alertStyles}>
                        {translate(ResourcesIds.CLINIC_MEMBERS_LOAD_ERROR)}
                    </Alert>
                )}

                {props.isLoading ? (
                    <Box sx={loadingBoxStyles}>
                        <CircularProgress />
                    </Box>
                ) : !props.hasLoaded ? null : props.members.length === 0 ? (
                    <Box sx={emptyStateBoxStyles}>
                        <Typography variant="body1" color="text.secondary">
                            {translate(ResourcesIds.CLINIC_MEMBERS_EMPTY)}
                        </Typography>
                    </Box>
                ) : isCompact ? (
                    // A table inside a dialog is the worst of the six: the dialog is
                    // already narrower than the screen, so on a phone this was a
                    // horizontal scroller nested inside a modal, and the role and status
                    // -- the two things anybody opens this to check -- were the columns
                    // past the edge.
                    <Box sx={cardListStyles}>
                        {props.members.map((member) => (
                            <RecordCard
                                key={member.id}
                                title={memberFullName(member)}
                                titleAdornment={
                                    <Chip
                                        size="small"
                                        color={STATUS_COLOURS[member.status]}
                                        label={translate(STATUS_LABELS[member.status])}
                                    />
                                }
                                isMuted={member.status !== 'ACTIVE'}
                                fields={[
                                    {
                                        label: translate(ResourcesIds.EMAIL_LABEL),
                                        value: member.emailAddress,
                                    },
                                    {
                                        label: translate(ResourcesIds.MEMBER_ROLE_LABEL),
                                        value: member.role ? translate(ROLE_LABELS[member.role]) : '',
                                    },
                                ]}
                            />
                        ))}
                    </Box>
                ) : (
                    <TableContainer sx={tableContainerStyles}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headerCellStyles}>
                                        {translate(ResourcesIds.MEMBER_NAME_LABEL)}
                                    </TableCell>
                                    <TableCell sx={headerCellStyles}>
                                        {translate(ResourcesIds.EMAIL_LABEL)}
                                    </TableCell>
                                    <TableCell sx={headerCellStyles}>
                                        {translate(ResourcesIds.MEMBER_ROLE_LABEL)}
                                    </TableCell>
                                    <TableCell sx={headerCellStyles}>
                                        {translate(ResourcesIds.MEMBER_STATUS_LABEL)}
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {props.members.map((member) => (
                                    <TableRow
                                        key={member.id}
                                        sx={memberRowStyles(member.status === 'ACTIVE')}
                                    >
                                        <TableCell>{memberFullName(member)}</TableCell>
                                        <TableCell sx={identifierCellStyles}>
                                            {member.emailAddress}
                                        </TableCell>
                                        <TableCell>
                                            {member.role && (
                                                <Chip
                                                    size="small"
                                                    label={translate(ROLE_LABELS[member.role])}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={STATUS_COLOURS[member.status]}
                                                label={translate(STATUS_LABELS[member.status])}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions sx={dialogActionsStyles}>
                <Button onClick={props.onInviteClick} variant="contained">
                    {translate(ResourcesIds.INVITE_ADMIN_ACTION)}
                </Button>
                <Button onClick={props.onClose} variant="outlined">
                    {translate(ResourcesIds.CANCEL_TEXT)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
